/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import MultiWebSockets from '../../../../client/manager/MultiWebSockets'
import Action from '../../../../model/Action'
import Vatom from '../../../../model/BVatom'
import Face from '../../../../model/Face'
import Store from '../../../repo/Store'
import Client from '../../Client'

export default class VatomApi {
  static _instance: VatomApi

  client: Client
  store: Store
  webSockets: MultiWebSockets

  constructor(appID: string, server: string, websocketServer: string) {
    const store = new Store('native')
    store.appID = appID
    store.server = server || 'https://api.vi.vatom.network'
    store.websocketAddress = [websocketServer || 'wss://ws.vatominc.net']

    this.store = store
    this.client = new Client(store)

    // const userApi = new UserApi(this)
    this.webSockets = new MultiWebSockets(store, this.client)
    VatomApi._instance = this
  }

  static getInstance(appID: string, server: string, websocketServer: string) {
    return VatomApi._instance || new VatomApi(appID, server, websocketServer)
  }

  getActions(templateID: string) {
    return this.client.request('GET', `/v1/user/actions/${templateID}`, {}, true).then(data => {
      const len = data.length
      const actions = []
      for (let i = 0; i < len; ) {
        const action = data[i].name.split('::Action::')
        actions.push({
          template_id: action[0],
          action: action[1]
        })
        i += 1
      }
      return actions
    })
  }

  async performAction(action: string, payload: any) {
    try {
      const res = await this.client.request(
        'POST',
        `/v1/user/vatom/action/${action}`,
        payload,
        true
      )
      return res
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  getUserInventory(payload = {}) {
    return this.client.request('POST', '/v1/user/vatom/inventory', payload, true).then(data => {
      const { actions, faces, vatoms } = data

      const actionsArray: Action[] = []
      const facesArray: Face[] = []
      const vatomsArray = []
      // eslint-disable-next-line
      for (let a of actions) {
        const aName = a.name.split('::Action::')
        const aKey = aName[0]
        actionsArray.push(
          new Action({
            template: aKey,
            name: aName[1],
            meta: a.meta,
            properties: a.properties
          })
        )
      }
      // eslint-disable-next-line
      for (let f of faces) {
        facesArray.push(
          new Face({
            template: f.template,
            id: f.id,
            meta: f.meta,
            properties: f.properties
          })
        )
      }
      // eslint-disable-next-line
      for (let v of vatoms) {
        const { template } = v['vAtom::vAtomType']
        if (template !== 'vatomic::v1::vAtom::Avatar') {
          const obj = new Vatom(
            v,
            facesArray.filter(f => f.template === template),
            actionsArray.filter(a => a.template === template)
          )

          vatomsArray.push(obj)
        }
      }
      return vatomsArray
    })
  }

  getUserVatoms(payload: any) {
    return this.client.request('POST', '/v1/user/vatom/get', payload, true).then(data => {
      const { actions, faces, vatoms } = data

      const actionsArray: Action[] = []
      const facesArray: Face[] = []
      const vatomsArray = []

      for (const a of actions) {
        const aName = a.name.split('::Action::')
        const aKey = aName[0]
        actionsArray.push(
          new Action({
            template: aKey,
            name: aName[1],
            meta: a.meta,
            properties: a.properties
          })
        )
      }

      for (const f of faces) {
        facesArray.push(
          new Face({
            template: f.template,
            id: f.id,
            meta: f.meta,
            properties: f.properties
          })
        )
      }

      for (const v of vatoms) {
        const { template } = v['vAtom::vAtomType']
        const obj = new Vatom(
          v,
          facesArray.filter(f => f.template === template),
          actionsArray.filter(a => a.template === template)
        )
        vatomsArray.push(obj)
      }
      return vatomsArray
    })
  }

  geoDiscover(payload: any) {
    return this.client.request('POST', '/v1/vatom/geodiscover', payload, true).then(data => {
      const { actions, faces, vatoms } = data

      const actionsArray: Action[] = []
      const facesArray: Face[] = []
      const vatomsArray = []
      // eslint-disable-next-line
      for (let a of actions) {
        const aName = a.name.split('::Action::')
        const aKey = aName[0]
        actionsArray.push(
          new Action({
            template: aKey,
            name: aName[1],
            meta: a.meta,
            properties: a.properties
          })
        )
      }
      // eslint-disable-next-line
      for (let f of faces) {
        facesArray.push(
          new Face({
            template: f.template,
            id: f.id,
            meta: f.meta,
            properties: f.properties
          })
        )
      }
      // eslint-disable-next-line
      for (let v of vatoms) {
        const { template } = v['vAtom::vAtomType']
        const obj = new Vatom(
          v,
          facesArray.filter(f => f.template === template),
          actionsArray.filter(a => a.template === template)
        )
        vatomsArray.push(obj)
      }
      return vatomsArray
    })
  }

  geoDiscoverGroups(payload: any) {
    return this.client
      .request('POST', '/v1/vatom/geodiscovergroups', payload, true)
      .then(data => data)
  }

  getVatomChildren(parentID: string) {
    return this.client
      .request(
        'POST',
        '/v1/vatom/discover',
        {
          scope: {
            key: 'vAtom::vAtomType.parent_id',
            value: parentID
          },
          return: {
            type: '*',
            fields: []
          }
        },
        true
      )
      .then(data => {
        const { actions, faces, results } = data
        const vatoms = results

        const actionsArray: Action[] = []
        const facesArray: Face[] = []
        const vatomsArray = []
        // eslint-disable-next-line
        for (let a of actions) {
          const aName = a.name.split('::Action::')
          const aKey = aName[0]
          actionsArray.push(
            new Action({
              template: aKey,
              name: aName[1],
              meta: a.meta,
              properties: a.properties
            })
          )
        }
        // eslint-disable-next-line
        for (let f of faces) {
          facesArray.push(
            new Face({
              template: f.template,
              id: f.id,
              meta: f.meta,
              properties: f.properties
            })
          )
        }
        // eslint-disable-next-line
        for (let v of vatoms) {
          const { template } = v['vAtom::vAtomType']
          const obj = new Vatom(
            v,
            facesArray.filter(f => f.template === template),
            actionsArray.filter(a => a.template === template)
          )
          vatomsArray.push(obj)
        }
        return vatomsArray
      })
  }

  setParent(payload: any) {
    return this.client.request('PATCH', '/v1/vatoms', payload, true).then(data => data)
  }

  trashVatom(vatomID: string) {
    const payload = {
      'this.id': vatomID
    }
    return this.client.request('POST', '/v1/user/vatom/trash', payload, true).then(data => data)
  }

  encodeAssetProvider(url: string) {
    if (!url || url.includes('undefined')) return url
    const aP = this.store.assetProvider
    const aPlen = aP.length
    const compare = new URL(url)
    for (let i = 0; i < aPlen; i += 1) {
      const comparethis = new URL(aP[i].uri)
      if (compare.hostname === comparethis.hostname) {
        // same uri so get the policy signature and key and append
        const queryString = VatomApi.mapString(aP[i].descriptor)
        return `${url}?${queryString}`
      }
    }
    return url
  }

  static mapString(o: any) {
    return Object.keys(o)
      .map(key => `${key}=${o[key]}`)
      .join('&')
  }
}
